// P40 QA-28 / QA-29 / QA-30 -- whether the browser is online, from the
// browser's own signal only: `navigator.onLine` and the window's `online` /
// `offline` events. Nothing is inferred from a failed request (a refused or
// timed-out call is still answered by its own error path), so this never
// claims "offline" for a connection the browser considers up.
//
// One observable for the whole app; consumers read `isOnline()` (reactive
// in computed properties) or subscribe to the return of the connection.

import Vue from 'vue'

const state = Vue.observable({ online: true })
const reconnectListeners = new Set()
let installedOn = null

function read(win) {
  const nav = win && win.navigator
  return !(nav && nav.onLine === false)
}

function onOnline() {
  state.online = true
  reconnectListeners.forEach(listener => {
    try {
      listener()
    } catch (err) {
      // one listener never stops the others
    }
  })
}

function onOffline() {
  state.online = false
}

/**
 * Starts following the browser's connectivity (idempotent per window).
 * @param {Window} [win]
 */
export function installConnectivity(
  win = typeof window !== 'undefined' ? window : undefined
) {
  if (!win || installedOn === win) return
  if (installedOn) uninstallConnectivity()
  installedOn = win
  state.online = read(win)
  win.addEventListener('online', onOnline)
  win.addEventListener('offline', onOffline)
}

export function uninstallConnectivity() {
  if (!installedOn) return
  installedOn.removeEventListener('online', onOnline)
  installedOn.removeEventListener('offline', onOffline)
  installedOn = null
  state.online = true
}

/** @returns {boolean} false only while the browser says it is offline */
export function isOnline() {
  if (!installedOn) installConnectivity()
  return state.online
}

/**
 * Calls `listener` each time the browser comes back online.
 * @param {() => void} listener
 * @returns {() => void} unsubscribe
 */
export function onReconnect(listener) {
  if (!installedOn) installConnectivity()
  reconnectListeners.add(listener)
  return () => reconnectListeners.delete(listener)
}
